<section class="main-content wrapper content-padding product-import-page">
  <div class="content-panel">
    <div class="page-header">
      <div>
        <span class="eyebrow">Product Management</span>
        <h2>Product Import</h2>
        <p>
          Upload a CSV or Excel file, validate rows, fix issues, and import products safely.
        </p>
      </div>

      <div class="page-header-actions">
        <button type="button" class="btn btn-soft" (click)="downloadTemplate()">
          <i class="fas fa-download"></i>
          Download Template
        </button>

        <button type="button" class="btn btn-ghost" (click)="reset()">
          <i class="fas fa-redo-alt"></i>
          Reset
        </button>
      </div>
    </div>

    <section class="import-page">
      <div class="step-strip">
        <div class="step-item active">
          <span>1</span>
          <div>
            <strong>Upload</strong>
            <small>Select your file</small>
          </div>
        </div>

        <div class="step-line"></div>

        <div class="step-item" [class.active]="rows.length">
          <span>2</span>
          <div>
            <strong>Validate</strong>
            <small>Review records</small>
          </div>
        </div>

        <div class="step-line"></div>

        <div class="step-item" [class.active]="getReadyCount() > 0">
          <span>3</span>
          <div>
            <strong>Import</strong>
            <small>Submit valid data</small>
          </div>
        </div>
      </div>

      <div class="top-grid">
        <div
          class="upload-card"
          [class.dragging]="isDragging"
          [class.has-file]="selectedFileName"
          (drop)="onDrop($event)"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
        >
          <input
            type="file"
            id="productImportFile"
            accept=".xlsx,.xls,.csv"
            hidden
            (change)="onFileChange($event)"
          />

          <label for="productImportFile" class="upload-inner">
            <div class="upload-icon">
              <i class="fas fa-cloud-upload-alt"></i>
            </div>

            <h3>{{ selectedFileName ? "File Selected" : "Upload Product File" }}</h3>

            <p>
              Drag and drop your Excel or CSV file here, or click below to browse.
            </p>

            <span class="btn btn-primary">
              <i class="fas fa-folder-open"></i>
              {{ isUploading ? "Uploading..." : selectedFileName ? "Change File" : "Choose File" }}
            </span>

            <div class="selected-file" *ngIf="selectedFileName">
              <i class="fas fa-file-excel"></i>
              <span>{{ selectedFileName }}</span>
            </div>

            <small>Supported formats: .xlsx, .xls, .csv</small>
          </label>
        </div>

        <div class="side-panel">
          <div class="help-card">
            <div class="help-icon">
              <i class="fas fa-lightbulb"></i>
            </div>

            <div>
              <h3>Before Import</h3>
              <p>
                Use the template and reference data to avoid category, partner, and product mapping errors.
              </p>
            </div>
          </div>

          <div class="options-card">
            <h3>Import Options</h3>

            <label style="display: none;">
              <input type="checkbox" [(ngModel)]="options.skipDuplicates" />
              <span>
                <strong>Skip duplicate SKU</strong>
                <small>Ignore products already existing with the same SKU.</small>
              </span>
            </label>

            <label>
              <input type="checkbox" [(ngModel)]="options.updateExisting" />
              <span>
                <strong>Update existing product by SKU</strong>
                <small>Use SKU to update matched products.</small>
              </span>
            </label>

            <label>
              <input type="checkbox" [(ngModel)]="options.importValidOnly" />
              <span>
                <strong>Import valid rows only</strong>
                <small>Skip incorrect rows during final import.</small>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="columns-card" style="display: none;">
        <div class="section-title">
          <div>
            <h3>Supported Columns</h3>
            <p>Required columns are marked with an asterisk. Use exact column names in your file.</p>
          </div>
        </div>

        <div class="column-list">
          <span
            *ngFor="let column of columns"
            class="column-pill"
            [class.required]="column.required"
          >
            {{ column.key }}
            <b *ngIf="column.required">*</b>
          </span>
        </div>
      </div>

      <div class="references-card">
        <div class="references-header">
          <div>
            <h3>Import References</h3>
            <p>
              Select a partner to view only the product categories available for that partner.
            </p>
          </div>

          <div class="reference-actions">
            <button
              type="button"
              class="btn btn-outline"
              [disabled]="isLoadingReferences"
              (click)="loadReferences()"
            >
              <i class="fas fa-sync-alt" [class.fa-spin]="isLoadingReferences"></i>
              {{ isLoadingReferences ? "Loading..." : "Refresh" }}
            </button>

            <button
              type="button"
              class="btn btn-outline"
              (click)="showReferences = !showReferences"
            >
              <i class="fas" [ngClass]="showReferences ? 'fa-eye-slash' : 'fa-eye'"></i>
              {{ showReferences ? "Hide" : "Show" }}
            </button>
          </div>
        </div>

        <div class="reference-partner-filter" *ngIf="showReferences">
          <div>
            <label>Filter categories by partner</label>
            <select
              [(ngModel)]="selectedReferencePartnerId"
              (change)="onReferencePartnerChange()"
            >
              <option value="">All categories</option>
              <option *ngFor="let partner of partners" [value]="partner.id">
                {{ partner.businessName }}
              </option>
            </select>
          </div>

          <p>
            Category list will change based on selected partner. Use the copied ID or exact name in Excel.
          </p>
        </div>

        <div class="references-body" *ngIf="showReferences">
          <div class="reference-column">
            <div class="reference-title">
              <div>
                <h4>Product Categories</h4>
                <small *ngIf="selectedReferencePartnerId">
                  Showing categories for selected partner
                </small>
                <small *ngIf="!selectedReferencePartnerId">
                  Showing all categories
                </small>
              </div>

              <button
                type="button"
                class="mini-btn"
                (click)="downloadCategoryReferenceCsv()"
              >
                Export CSV
              </button>
            </div>

            <div class="reference-search">
              <i class="fas fa-search"></i>
              <input
                type="search"
                placeholder="Search category ID or name..."
                [(ngModel)]="categoryReferenceSearch"
              />
            </div>

            <div class="reference-table-wrap">
              <table class="reference-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Category Name</th>
                    <th>Copy</th>
                  </tr>
                </thead>

                <tbody>
                  <tr *ngIf="!filteredReferenceCategories.length">
                    <td colspan="3" class="empty-reference">
                      No categories found for this partner/search.
                    </td>
                  </tr>

                  <tr *ngFor="let category of filteredReferenceCategories">
                    <td>{{ category.id }}</td>
                    <td>{{ category.categoryName }}</td>
                    <td>
                      <button
                        type="button"
                        class="copy-btn"
                        (click)="copyText(category.id)"
                      >
                        ID
                      </button>

                      <button
                        type="button"
                        class="copy-btn"
                        (click)="copyText(category.categoryName)"
                      >
                        Name
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="reference-column">
            <div class="reference-title">
              <div>
                <h4>Partners</h4>
                <small>Search partner ID or business name</small>
              </div>

              <button
                type="button"
                class="mini-btn"
                (click)="downloadPartnerReferenceCsv()"
              >
                Export CSV
              </button>
            </div>

            <div class="reference-search">
              <i class="fas fa-search"></i>
              <input
                type="search"
                placeholder="Search partner ID or name..."
                [(ngModel)]="partnerReferenceSearch"
              />
            </div>

            <div class="reference-table-wrap">
              <table class="reference-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Business Name</th>
                    <th>Copy</th>
                  </tr>
                </thead>

                <tbody>
                  <tr *ngIf="!filteredReferencePartners.length">
                    <td colspan="3" class="empty-reference">
                      No partners found.
                    </td>
                  </tr>

                  <tr *ngFor="let partner of filteredReferencePartners">
                    <td>{{ partner.id }}</td>
                    <td>{{ partner.businessName }}</td>
                    <td>
                      <button
                        type="button"
                        class="copy-btn"
                        (click)="copyText(partner.id)"
                      >
                        ID
                      </button>

                      <button
                        type="button"
                        class="copy-btn"
                        (click)="copyText(partner.businessName)"
                      >
                        Name
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="reference-column">
            <div class="reference-title">
              <div>
                <h4>Existing Products</h4>
                <small>Use for super product mapping</small>
              </div>

              <button
                type="button"
                class="mini-btn"
                (click)="downloadProductReferenceCsv()"
              >
                Export CSV
              </button>
            </div>

            <div class="reference-search">
              <i class="fas fa-search"></i>
              <input
                type="search"
                placeholder="Search product ID, name, or SKU..."
                [(ngModel)]="productReferenceSearch"
              />
            </div>

            <div class="reference-table-wrap">
              <table class="reference-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>SKU</th>
                    <th>Copy</th>
                  </tr>
                </thead>

                <tbody>
                  <tr *ngIf="!filteredReferenceProducts.length">
                    <td colspan="4" class="empty-reference">
                      No products found.
                    </td>
                  </tr>

                  <tr *ngFor="let product of filteredReferenceProducts">
                    <td>{{ product.id }}</td>
                    <td>{{ product.name }}</td>
                    <td>{{ product.sku }}</td>
                    <td>
                      <button
                        type="button"
                        class="copy-btn"
                        (click)="copyText(product.id)"
                      >
                        ID
                      </button>

                      <button
                        type="button"
                        class="copy-btn"
                        (click)="copyText(product.sku)"
                      >
                        SKU
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-card total">
          <div class="summary-icon">
            <i class="fas fa-layer-group"></i>
          </div>
          <div>
            <span>Total Records</span>
            <strong>{{ summary.total }}</strong>
          </div>
        </div>

        <div class="summary-card success">
          <div class="summary-icon">
            <i class="fas fa-check"></i>
          </div>
          <div>
            <span>Valid Records</span>
            <strong>{{ summary.valid }}</strong>
          </div>
        </div>

        <div class="summary-card danger">
          <div class="summary-icon">
            <i class="fas fa-times"></i>
          </div>
          <div>
            <span>Invalid Records</span>
            <strong>{{ summary.invalid }}</strong>
          </div>
        </div>

        <div class="summary-card warning">
          <div class="summary-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div>
            <span>Warnings</span>
            <strong>{{ summary.warning }}</strong>
          </div>
        </div>
      </div>

      <div class="table-panel">
        <div class="table-toolbar">
          <div class="validation-heading">
            <h3>Validation Preview</h3>

            <p class="validation-subtitle">
              Review product rows before final import.
            </p>

            <div class="validation-image-note">
              <div class="validation-image-note-icon">
                <i class="fas fa-image"></i>
              </div>

              <div>
                <strong>Image upload note</strong>

                <p>
                  Use a direct public image URL in the <b>image</b> column.
                  Example:
                  <span>https://example.com/product.jpg</span>
                </p>

                <p>
                  Do not use Google Drive preview links, webpage links, or embedded Excel images.
                  The importer can download only direct image URLs.
                </p>
              </div>
            </div>
          </div>

          <div class="toolbar-actions">
            <input
              type="search"
              class="search-input"
              placeholder="Search product, SKU, category..."
              [(ngModel)]="searchText"
              (ngModelChange)="applyFilters()"
            />
          </div>
        </div>

        <div class="filter-tabs">
          <button
            type="button"
            [class.active]="activeTab === 'all'"
            (click)="setTab('all')"
          >
            All
            <span>{{ summary.total }}</span>
          </button>

          <button
            type="button"
            [class.active]="activeTab === 'valid'"
            (click)="setTab('valid')"
          >
            Correct
            <span>{{ summary.valid }}</span>
          </button>

          <button
            type="button"
            [class.active]="activeTab === 'invalid'"
            (click)="setTab('invalid')"
          >
            Incorrect
            <span>{{ summary.invalid }}</span>
          </button>

          <button
            type="button"
            [class.active]="activeTab === 'warning'"
            (click)="setTab('warning')"
          >
            Warnings
            <span>{{ summary.warning }}</span>
          </button>
        </div>

        <!-- added -->

        <div class="records-summary" *ngIf="summary.total">
          <div class="record-count">
            <strong>{{ summary.total | number }}</strong>
            <span>records found in uploaded file</span>
          </div>
        
          <div class="visible-count">
            Showing
            <strong>{{ firstVisibleRow | number }}</strong>
            to
            <strong>{{ lastVisibleRow | number }}</strong>
            of
            <strong>{{ filteredRows.length | number }}</strong>
            matching records
          </div>
        </div>
        <!--  -->

        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th class="sticky-col row-col">Row</th>
                <th class="sticky-col status-col">Status</th>

                <th
                  *ngFor="let column of columns"
                  [style.minWidth.px]="column.width"
                >
                  {{ column.label }}
                  <span *ngIf="column.required">*</span>
                </th>

                <th class="message-header">Messages</th>
              </tr>
            </thead>

            <tbody>
              <tr *ngIf="!filteredRows.length">
                <td [attr.colspan]="columns.length + 3" class="empty-cell">
                  <div class="empty-state">
                    <i class="fas fa-file-import"></i>
                    <strong>No records found</strong>
                    <span>Upload a file  to start validation.</span>
                  </div>
                </td>
              </tr>

              <tr
                *ngFor="let row of displayedRows; trackBy: trackByRow"
                [class.row-invalid]="row.status === 'invalid'"
                [class.row-warning]="row.status === 'warning'"
              >
                <td class="sticky-col row-col row-no">#{{ row.rowNumber }}</td>

                <td class="sticky-col status-col">
                  <span class="status" [ngClass]="row.status">
                    {{ getStatusLabel(row) }}
                  </span>
                </td>

                <td
                  *ngFor="let column of columns"
                  [class.cell-error]="hasError(row, column.key)"
                  [class.cell-warning]="hasWarning(row, column.key)"
                >
                  <textarea
                    class="cell-input"
                    rows="2"
                    [ngModel]="row[column.key]"
                    (ngModelChange)="updateCell(row, column.key, $event)"
                  ></textarea>

                  <small class="field-error" *ngIf="getFieldError(row, column.key)">
                    {{ getFieldError(row, column.key) }}
                  </small>

                  <small class="field-warning" *ngIf="getFieldWarning(row, column.key)">
                    {{ getFieldWarning(row, column.key) }}
                  </small>
                </td>

                <td class="message-cell">
                  <div *ngFor="let error of row.errors" class="message error">
                    <i class="fas fa-times-circle"></i>
                    <span><b>{{ error.field }}:</b> {{ error.message }}</span>
                  </div>

                  <div *ngFor="let warning of row.warnings" class="message warning">
                    <i class="fas fa-exclamation-circle"></i>
                    <span><b>{{ warning.field }}:</b> {{ warning.message }}</span>
                  </div>

                  <span
                    *ngIf="
                      (!row.errors || !row.errors.length) &&
                      (!row.warnings || !row.warnings.length)
                    "
                    class="clean-message"
                  >
                    <i class="fas fa-check-circle"></i>
                    Clean
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- added -->

        <div class="import-pagination" *ngIf="filteredRows.length">
          <div class="pagination-size">
            <label for="importPageSize">Rows per page</label>
        
            <select
              id="importPageSize"
              [(ngModel)]="pageSize"
              (change)="onPageSizeChange()"
            >
              <option
                *ngFor="let size of pageSizeOptions"
                [ngValue]="size"
              >
                {{ size }}
              </option>
            </select>
          </div>
        
          <div class="pagination-controls">
            <button
              type="button"
              class="pagination-btn"
              [disabled]="page === 1"
              (click)="previousPage()"
            >
              <i class="fas fa-chevron-left"></i>
              Previous
            </button>
        
            <span class="pagination-page">
              Page
              <strong>{{ page }}</strong>
              of
              <strong>{{ totalPages }}</strong>
            </span>
        
            <button
              type="button"
              class="pagination-btn"
              [disabled]="page === totalPages"
              (click)="nextPage()"
            >
              Next
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
<!--  -->
        <div class="footer-actions">
          <div class="ready-count">
            <span>Ready for import</span>
            <strong>{{ getReadyCount() }}</strong>
            <small>products can be imported now.</small>
          </div>

          <div class="footer-buttons">
            <button
              type="button"
              class="btn btn-outline"
              [disabled]="!rows.length || isValidating"
              (click)="revalidateRows()"
            >
              <i class="fas fa-check-double"></i>
              {{ isValidating ? "Validating..." : "Revalidate" }}
            </button>

            <button
              type="button"
              class="btn btn-primary"
              [disabled]="!rows.length || isImporting"
              (click)="importProducts()"
            >
              <i class="fas fa-upload"></i>
              {{ isImporting ? "Importing..." : "Final Import" }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</section>

